/**
 * Lead Supply manual-evidence and property-address research actions.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError, getDashboardOperator } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import type { LeadSupplyManualEvidenceUploadManifestRow } from "./dashboard-lead-factory-types.js";
import {
  leadFactoryManualEvidenceStatusUrl,
  leadFactoryPropertyAddressResearchUrl,
  leadFactoryReferencedDeedUploadUrl,
  leadFactoryReferencedDeedUrl,
  leadFactorySourceDocumentReviewDispositionUrl
} from "./dashboard-urls.js";

const propertyAddressResearchSources = ["tax_record", "parcel_record", "county_gis", "pi_report", "skip_trace", "alternate_source"] as const;

export async function submitLeadSupplyManualEvidenceStatus(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const taskId = form.dataset.taskId;
  if (!taskId) return;
  const acquisitionStatus = form.querySelector<HTMLSelectElement>("[data-lead-supply-manual-evidence-status]")?.value.trim() || "";
  if (!acquisitionStatus) {
    statusElement.textContent = `${taskId}: choose a manual evidence acquisition status.`;
    return;
  }
  const sourceUrl = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-source-url]")?.value.trim() || "";
  const nextDueAt = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-next-due-at]")?.value.trim() || "";
  const note =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-note]")?.value.trim() ||
    "Manual original Deed of Trust acquisition status recorded from Lead Supply.";
  statusElement.textContent = `${taskId}: recording manual original D/T acquisition status...`;
  try {
    const response = await authenticatedFetch(leadFactoryManualEvidenceStatusUrl(taskId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        acquisitionStatus,
        sourceUrl: sourceUrl || undefined,
        nextDueAt: nextDueAt || undefined,
        note
      })
    });
    const body = (await response.json()) as { error?: string; acquisitionStatus?: string; status?: string; taskId?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${taskId}: recorded ${labelize(body.acquisitionStatus ?? acquisitionStatus)}; task remains ${labelize(body.status ?? "open")}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Manual original D/T acquisition status failed.";
  }
}

export async function submitLeadSupplyManualEvidenceRequestStatus(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const taskIds = (form.dataset.taskIds || "").split(",").map((id) => id.trim()).filter(Boolean);
  if (taskIds.length === 0) return;
  const acquisitionStatus = form.querySelector<HTMLSelectElement>("[data-lead-supply-manual-evidence-status]")?.value.trim() || "";
  if (!acquisitionStatus) {
    statusElement.textContent = "Choose a grouped manual original D/T acquisition status.";
    return;
  }
  const sourceUrl = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-source-url]")?.value.trim() || "";
  const nextDueAt = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-next-due-at]")?.value.trim() || "";
  const referencedBookPage = form.dataset.referencedBookPage || "request";
  const note =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-note]")?.value.trim() ||
    `Grouped manual original Deed of Trust acquisition status recorded for ${referencedBookPage}.`;
  statusElement.textContent = `${referencedBookPage}: recording ${labelize(acquisitionStatus)} on ${formatInteger(taskIds.length)} task${taskIds.length === 1 ? "" : "s"}...`;
  try {
    let completed = 0;
    for (const taskId of taskIds) {
      const response = await authenticatedFetch(leadFactoryManualEvidenceStatusUrl(taskId), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          acquisitionStatus,
          sourceUrl: sourceUrl || undefined,
          nextDueAt: nextDueAt || undefined,
          note
        })
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `${taskId}: request failed with ${response.status}`);
      completed += 1;
    }
    statusElement.textContent = `${referencedBookPage}: recorded ${labelize(acquisitionStatus)} on ${formatInteger(completed)} task${completed === 1 ? "" : "s"}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Grouped manual original D/T acquisition status failed.";
  }
}

export async function submitLeadSupplyManualEvidenceRequestUpload(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const rows = parseManualEvidenceUploadRows(form.dataset.uploadManifestRows || "[]");
  const referencedBookPage = form.dataset.referencedBookPage || "request";
  if (rows.length === 0) {
    statusElement.textContent = `${referencedBookPage}: no upload rows were found for this request.`;
    return;
  }
  const file = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-file]")?.files?.[0];
  if (!file) {
    statusElement.textContent = `${referencedBookPage}: choose the acquired original D/T PDF before uploading.`;
    return;
  }
  const filedDate = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-filed-date]")?.value.trim() || "";
  if (!filedDate) {
    statusElement.textContent = `${referencedBookPage}: enter the original D/T filed date before uploading.`;
    return;
  }
  const sourceUrl = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-source-url]")?.value.trim() || "";
  const note =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-upload-note]")?.value.trim() ||
    `Grouped manual original Deed of Trust PDF uploaded for ${referencedBookPage}.`;
  statusElement.textContent = `${referencedBookPage}: uploading one PDF across ${formatInteger(rows.length)} task-linked row${rows.length === 1 ? "" : "s"}...`;
  try {
    let completed = 0;
    for (const row of rows) {
      if (!row.sourceDocumentId) throw new Error(`${referencedBookPage}: upload row is missing sourceDocumentId.`);
      const upload = new FormData();
      upload.set("sourceDocumentId", row.sourceDocumentId);
      upload.set("leadId", row.leadId || "");
      upload.set("manualEvidenceTaskId", row.manualEvidenceTaskId || row.taskId || "");
      upload.set("filedDate", filedDate);
      upload.set("sourceUrl", sourceUrl || row.sourceUrl || "");
      upload.set("note", note);
      upload.set("file", file);
      const response = await authenticatedFetch(leadFactoryReferencedDeedUploadUrl, {
        method: "POST",
        body: upload
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `${row.sourceDocumentId}: request failed with ${response.status}`);
      completed += 1;
    }
    statusElement.textContent = `${referencedBookPage}: uploaded PDF across ${formatInteger(completed)} task-linked row${completed === 1 ? "" : "s"}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Grouped manual original D/T upload failed.";
  }
}

export function parseManualEvidenceUploadRows(raw: string): LeadSupplyManualEvidenceUploadManifestRow[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => (row && typeof row === "object" ? (row as LeadSupplyManualEvidenceUploadManifestRow) : null))
      .filter((row): row is LeadSupplyManualEvidenceUploadManifestRow => Boolean(row));
  } catch {
    return [];
  }
}

export async function submitLeadSupplyManualEvidenceRequestCorrection(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const rows = parseManualEvidenceUploadRows(form.dataset.uploadManifestRows || "[]");
  const referencedBookPage = form.dataset.referencedBookPage || "request";
  if (rows.length === 0) {
    statusElement.textContent = `${referencedBookPage}: no source rows were found for this request.`;
    return;
  }
  const referencedDeedBook = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-corrected-book]")?.value.trim() || "";
  const referencedDeedPage = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-corrected-page]")?.value.trim() || "";
  if (!referencedDeedBook || !referencedDeedPage) {
    statusElement.textContent = `${referencedBookPage}: enter corrected book and page before preparing retrieval.`;
    return;
  }
  const sourcePage = form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-corrected-source-page]")?.value.trim() || "";
  const evidenceExcerpt =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-corrected-evidence]")?.value.trim() ||
    `Corrected original Deed of Trust coordinates from manual evidence request ${referencedBookPage}.`;
  statusElement.textContent = `${referencedBookPage}: capturing corrected ${referencedDeedBook}/${referencedDeedPage} across ${formatInteger(rows.length)} row${rows.length === 1 ? "" : "s"}...`;
  try {
    let completed = 0;
    for (const row of rows) {
      if (!row.sourceDocumentId) throw new Error(`${referencedBookPage}: correction row is missing sourceDocumentId.`);
      const response = await authenticatedFetch(leadFactoryReferencedDeedUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          sourceDocumentId: row.sourceDocumentId,
          leadId: row.leadId || undefined,
          limit: 1,
          referencedDeedBook,
          referencedDeedPage,
          sourcePage: sourcePage || undefined,
          evidenceExcerpt
        })
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `${row.sourceDocumentId}: request failed with ${response.status}`);
      completed += 1;
    }
    statusElement.textContent = `${referencedBookPage}: captured corrected ${referencedDeedBook}/${referencedDeedPage} across ${formatInteger(completed)} row${completed === 1 ? "" : "s"}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Grouped manual original D/T coordinate correction failed.";
  }
}

export async function submitLeadSupplyManualEvidenceRequestAlternateResearch(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const rows = parseManualEvidenceUploadRows(form.dataset.uploadManifestRows || "[]");
  const referencedBookPage = form.dataset.referencedBookPage || "request";
  if (rows.length === 0) {
    statusElement.textContent = `${referencedBookPage}: no source rows were found for alternate research routing.`;
    return;
  }
  const evidenceExcerpt =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-alternate-evidence]")?.value.trim() || "";
  if (evidenceExcerpt.length < 12) {
    statusElement.textContent = `${referencedBookPage}: add evidence explaining why alternate research is needed.`;
    return;
  }
  const assignedTo =
    form.querySelector<HTMLInputElement>("[data-lead-supply-manual-evidence-request-alternate-assignee]")?.value.trim() || getDashboardOperator();
  statusElement.textContent = `${referencedBookPage}: routing ${formatInteger(rows.length)} row${rows.length === 1 ? "" : "s"} to alternate property research...`;
  try {
    let completed = 0;
    for (const row of rows) {
      if (!row.sourceDocumentId) throw new Error(`${referencedBookPage}: alternate research row is missing sourceDocumentId.`);
      const response = await authenticatedFetch(leadFactorySourceDocumentReviewDispositionUrl(row.sourceDocumentId), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          outcome: "property_address_not_in_source",
          evidenceExcerpt,
          assignedTo,
          note: `Manual original D/T ${referencedBookPage} is unavailable; routed Lead Supply request to alternate property-address research.`
        })
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `${row.sourceDocumentId}: request failed with ${response.status}`);
      completed += 1;
    }
    statusElement.textContent = `${referencedBookPage}: routed ${formatInteger(completed)} row${completed === 1 ? "" : "s"} to alternate property research. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Grouped manual original D/T alternate research routing failed.";
  }
}

export async function submitPropertyAddressResearch(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const taskId = form.dataset.taskId?.trim() || "";
  if (!taskId) return;
  const propertyAddress = form.querySelector<HTMLInputElement>("[data-property-address-research-address]")?.value.trim() || "";
  if (!propertyAddress) {
    statusElement.textContent = `${taskId}: enter the researched property address.`;
    return;
  }
  const researchSource = form.querySelector<HTMLSelectElement>("[data-property-address-research-source]")?.value.trim() || "";
  if (!propertyAddressResearchSources.includes(researchSource as never)) {
    statusElement.textContent = `${taskId}: choose a typed research source.`;
    return;
  }
  const confidenceValue = form.querySelector<HTMLInputElement>("[data-property-address-research-confidence]")?.value.trim() || "";
  if (!confidenceValue) {
    statusElement.textContent = `${taskId}: enter a confidence value between 0 and 1.`;
    return;
  }
  const confidence = Number(confidenceValue);
  if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
    statusElement.textContent = `${taskId}: confidence must be between 0 and 1.`;
    return;
  }
  const evidence = form.querySelector<HTMLInputElement>("[data-property-address-research-evidence]")?.value.trim() || "";
  if (evidence.length < 12) {
    statusElement.textContent = `${taskId}: add tax/GIS/PI evidence for the researched address.`;
    return;
  }
  statusElement.textContent = `${taskId}: capturing researched property address...`;
  try {
    const response = await authenticatedFetch(leadFactoryPropertyAddressResearchUrl(taskId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        propertyAddress,
        normalizedAddress: form.querySelector<HTMLInputElement>("[data-property-address-research-normalized]")?.value.trim() || undefined,
        researchSource,
        sourceUrl: form.querySelector<HTMLInputElement>("[data-property-address-research-source-url]")?.value.trim() || undefined,
        parcelId: form.querySelector<HTMLInputElement>("[data-property-address-research-parcel-id]")?.value.trim() || undefined,
        confidence,
        evidence,
        note: "Property address research captured from Lead Supply."
      })
    });
    const body = (await response.json()) as {
      error?: string;
      propertyLeadId?: string;
      contactReadiness?: string | null;
      status?: string | null;
      resolvedBlockingReasons?: string[];
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const resolved = body.resolvedBlockingReasons?.length ? `; resolved ${body.resolvedBlockingReasons.map(labelize).join(", ")}` : "";
    const readiness = body.contactReadiness ? `; readiness ${labelize(body.contactReadiness)}` : "";
    const leadStatus = body.status ? `; lead ${labelize(body.status)}` : "";
    statusElement.textContent = `${body.propertyLeadId ?? taskId}: captured researched address${resolved}${readiness}${leadStatus}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Property address research capture failed.";
  }
}

